import { useCallback, useEffect, useRef, useState } from "react";
import { useDataProvider } from "react-admin";
import { employeeRequests } from "api/employeeApi";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";

/**
 * Loads the employment contracts a leave request can be filed against.
 *
 * The endpoint only returns data for the Su tenant; every other tenant — and every employee
 * without contracts — gets an empty array. Callers must treat that as "no contract step at all"
 * and let the request through, same as when the fetch fails.
 *
 * @param {number|string} [employeeId] the employee picked in the "Служител" field;
 *   when omitted, the logged-in user's own contracts are returned.
 * @returns {{ contracts: object[], isLoading: boolean, hasError: boolean }}
 */
export const useEmployeeContracts = employeeId => {
  const dataProvider = useDataProvider();
  const canPreview = checkPermission(getPermissions(), permissions.canPreviewLeaveRequests);
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(canPreview);
  const [hasError, setHasError] = useState(false);
  // Only the latest request may write state — switching employees fires a new one mid-flight.
  const requestIdRef = useRef(0);

  const fetchContracts = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    if (!canPreview) {
      setContracts([]);
      setHasError(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setHasError(false);
    try {
      const { data } = await dataProvider.customRequest(
        employeeRequests.getEmployeeContracts(employeeId),
        null,
        "GET",
      );
      if (isStale()) return;
      setContracts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (isStale()) return;
      // Non-blocking: the contract step is skipped entirely and the request still goes through.
      console.error(err);
      setContracts([]);
      setHasError(true);
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [employeeId, canPreview, dataProvider]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return { contracts, isLoading, hasError };
};
